/**
 * KeyboardHelpOverlay — app-wide "?" cheat-sheet.
 *
 * Mounted once in the AppShell. Pressing "?" (when not typing in a field)
 * toggles a modal listing every keyboard shortcut, so new counter staff learn
 * the keyboard flow — where POS speed actually comes from — in a day.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/Dialog';

interface Shortcut {
  keys: string[];
  label: string;
}
interface Group {
  title: string;
  items: Shortcut[];
}

const GROUPS: Group[] = [
  {
    title: 'Anywhere',
    items: [
      { keys: ['?'], label: 'Show / hide this cheat-sheet' },
      { keys: ['Esc'], label: 'Back / close' },
    ],
  },
  {
    title: 'Products & cart',
    items: [
      { keys: ['/'], label: 'Focus product search' },
      { keys: ['Ctrl', 'K'], label: 'Focus product search' },
      { keys: ['↑', '↓', '←', '→'], label: 'Move between products' },
      { keys: ['Enter'], label: 'Add focused product' },
      { keys: ['F2'], label: 'Attach customer' },
      { keys: ['F4'], label: 'Park current cart' },
      { keys: ['F9'], label: 'Charge / go to checkout' },
    ],
  },
  {
    title: 'Checkout & tender',
    items: [
      { keys: ['F1'], label: 'Cash' },
      { keys: ['F2'], label: 'UPI' },
      { keys: ['F3'], label: 'Card' },
      { keys: ['F4'], label: 'Credit' },
      { keys: ['0–9', '.'], label: 'Enter amount' },
      { keys: ['Enter'], label: 'Add tender · complete when settled' },
      { keys: ['Ctrl', '⌫'], label: 'Clear amount' },
      { keys: ['F9'], label: 'Complete sale' },
    ],
  },
];

export function KeyboardHelpOverlay() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Toggle on "?" even from a focused field (the billing search is always
      // focused) — preventDefault stops the character from being typed. "?" is
      // effectively never wanted as literal input on a POS counter.
      if (e.key === '?' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent title="Keyboard shortcuts" className="w-[600px]">
        <div className="grid grid-cols-1 gap-x-8 gap-y-5 px-5 py-4 sm:grid-cols-3">
          {GROUPS.map((g) => (
            <div key={g.title}>
              <div className="mb-2 text-label uppercase tracking-wide text-fg-tertiary">
                {g.title}
              </div>
              <ul className="space-y-2">
                {g.items.map((s) => (
                  <li key={s.label + s.keys.join()} className="flex items-center justify-between gap-3">
                    <span className="text-body text-fg-secondary">{s.label}</span>
                    <span className="flex shrink-0 items-center gap-1">
                      {s.keys.map((k) => (
                        <Kbd key={k}>{k}</Kbd>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-stroke-hairline/10 px-5 py-3 text-label text-fg-tertiary">
          Press <Kbd>?</Kbd> anytime to toggle this.
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex min-w-[22px] items-center justify-center rounded border border-stroke-hairline/20 bg-bg-subtle px-1.5 py-0.5 text-label font-medium text-fg-secondary">
      {children}
    </kbd>
  );
}
